import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { LATEST_WEB_VERSION } from './deploymentsData';
import Gallery from './Gallery';

// Native shell only: the store-visible shell version next to the running web
// bundle version, for OTA debugging. nativeShell.js stashes the shell info on
// window.ckShellInfo (this component is web-shared, so it must not import
// native modules) and fires ck-shell-info when it lands. On the website
// neither the class nor the global exists and this renders nothing.
function ShellVersionLine() {
  const [shell, setShell] = useState(window.ckShellInfo);
  useEffect(() => {
    const onInfo = () => setShell(window.ckShellInfo);
    window.addEventListener('ck-shell-info', onInfo);
    return () => window.removeEventListener('ck-shell-info', onInfo);
  }, []);
  if (!document.body.classList.contains('native-shell')) return null;
  return (
    <div style={{ ...S.footerTxt, marginTop: '4px' }}>
      {shell ? `shell ${shell.version} (${shell.build})` : 'shell —'} · web {LATEST_WEB_VERSION}
    </div>
  );
}

export default function NavDrawer({ user: userProp, onOpenGallery }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  // Pages that haven't loaded a set (everywhere except Checklist) don't pass
  // onOpenGallery; the drawer then opens the standalone Gallery itself.
  const [showGallery, setShowGallery] = useState(false);
  const [username, setUsername] = useState(null);
  // Some pages render <NavDrawer /> without a user prop (Marketplace, the
  // community pages) — resolve the session ourselves so signed-in items like
  // My Gallery don't silently disappear there.
  const [sessionUser, setSessionUser] = useState(null);
  useEffect(() => {
    if (userProp) return;
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => { if (!cancelled) setSessionUser(data?.user || null); });
    return () => { cancelled = true; };
  }, [userProp]);
  const user = userProp || sessionUser;

  useEffect(() => {
    if (!user?.id) return;
    const loadUnread = async () => {
      // RPC counts system messages (sender_id NULL) too, per participant.
      const { data } = await supabase.rpc('my_unread_counts');
      setUnread((data || []).reduce((n, r) => n + r.unread, 0));
    };
    loadUnread();
    const sub = supabase.channel('unread-badge')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, loadUnread)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'conversations' }, loadUnread)
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [user?.id]);
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    supabase.from('profiles').select('is_admin, username').eq('id', user.id).single()
      .then(({ data }) => { if (!cancelled) { setIsAdmin(!!data?.is_admin); setUsername(data?.username || null); } });
    return () => { cancelled = true; };
  }, [user?.id]);

  const drawerRef = useRef(null);

  const nav = path => {
    setOpen(false);
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // Close on outside click
  useEffect(() => {
    const handler = e => {
      if (open && drawerRef.current && !drawerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const currentPath = window.location.pathname;

  return (
    <>
      {/* Hamburger button — always top-right */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{ ...S.burger, borderColor: unread > 0 ? 'var(--cyan)' : 'var(--border)', boxShadow: unread > 0 ? '0 0 8px rgba(var(--cyan-rgb),0.3)' : 'none' }}
          aria-label="Open navigation"
        >
          <span style={{ ...S.bar, opacity: open ? 0 : 1, transform: open ? 'translateY(6px)' : 'none' }} />
          <span style={{ ...S.bar, width: open ? '22px' : '16px' }} />
          <span style={{ ...S.bar, opacity: open ? 0 : 1, transform: open ? 'translateY(-6px)' : 'none' }} />
        </button>
        {unread > 0 && (
          <div style={{ position: 'absolute', top: '-4px', right: '-4px', background: 'var(--red)', color: '#fff', fontFamily: "'Orbitron',monospace", fontSize: '8px', fontWeight: 700, borderRadius: '10px', minWidth: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', pointerEvents: 'none', lineHeight: 1 }}>
            {unread > 9 ? '9+' : unread}
          </div>
        )}
      </div>

      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        style={{ ...S.backdrop, opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none' }}
      />

      {/* Drawer */}
      <nav ref={drawerRef} style={{ ...S.drawer, transform: open ? 'translateX(0)' : 'translateX(100%)' }}>
        <div style={S.drawerHeader}>
          <span style={S.drawerLogo}>COLLECTION<span style={{ color: 'var(--brand-gold)' }}>KEEPER</span></span>
          <button onClick={() => setOpen(false)} style={S.closeBtn}>✕</button>
        </div>

        <div style={S.divider} />

        <div style={S.section}>
          <div style={S.sectionLabel}>NAVIGATE</div>

          <button
            onClick={() => nav('/')}
            style={{ ...S.navItem, ...(currentPath === '/' || currentPath.startsWith('/sets') ? S.navItemActive : {}) }}
          >
            <span style={S.navIcon}>☰</span>
            My Checklists
          </button>

         {user && (
           <button
            onClick={() => { setOpen(false); onOpenGallery ? onOpenGallery() : setShowGallery(true); }}
            style={S.navItem}
          >
            <span style={S.navIcon}>⬡</span>
            My Gallery
          </button>)}

          <button
            onClick={() => nav('/community')}
            style={{ ...S.navItem, ...(currentPath.startsWith('/community') ? S.navItemActive : {}) }}
          >
            <span style={S.navIcon}>◎</span>
            Community Checklists
          </button>

          <button
            onClick={() => nav('/marketplace')}
            style={{ ...S.navItem, ...(currentPath === '/marketplace' ? S.navItemActive : {}) }}
          >
            <span style={S.navIcon}>$</span>
            Marketplace
          </button>

          <button
            onClick={() => nav('/messages')}
            style={{ ...S.navItem, ...(currentPath.startsWith('/messages') ? S.navItemActive : {}), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={S.navIcon}>✉</span>
              Messages
            </span>
            {unread > 0 && <span style={{ background: 'var(--cyan)', color: 'var(--bg)', fontFamily: "'Orbitron',monospace", fontSize: '9px', fontWeight: 700, borderRadius: '10px', padding: '2px 7px' }}>{unread}</span>}
          </button>

          <button
            onClick={() => nav('/resources')}
            style={{ ...S.navItem, ...(currentPath.startsWith('/resources') ? S.navItemActive : {}) }}
          >
            <span style={S.navIcon}>⌬</span>
            Resources
          </button>
        </div>

        <div style={S.divider} />

        <div style={S.section}>
          <div style={S.sectionLabel}>ACCOUNT</div>

          <button
            onClick={() => nav('/profile')}
            style={S.navItem}
          >
            <span style={S.navIcon}>◈</span>
            Profile
          </button>

          {isAdmin && (
            <button
              onClick={() => nav('/admin')}
              style={{ ...S.navItem, ...(currentPath.startsWith('/admin') ? S.navItemActive : {}) }}
            >
              <span style={{ ...S.navIcon, color: 'var(--brand-gold)' }}>⛭</span>
              Admin
            </button>
          )}

          <button
            onClick={async () => { await supabase.auth.signOut(); window.location.href = '/'; }}
            style={{ ...S.navItem, color: 'var(--err)' }}
          >
            <span style={{ ...S.navIcon, color: 'var(--err)' }}>⏻</span>
            Sign Out
          </button>
        </div>

        <div style={S.drawerFooter}>
          <span style={S.footerTxt}>CollectionKeeper 2.0</span>
          <ShellVersionLine />
        </div>
      </nav>

      {showGallery && (
        <Gallery
          user={user}
          profile={{ username }}
          onClose={() => setShowGallery(false)}
        />
      )}
    </>
  );
}

const S = {
  burger: {
    position: 'fixed',
    top: 'calc(16px + max(env(safe-area-inset-top, 0px), var(--ck-android-inset-top, 0px)))',
    right: '16px',
    zIndex: 1100,
    background: 'rgba(var(--bg-rgb),0.85)',
    border: '1px solid var(--border)',
    borderRadius: '3px',
    width: '40px',
    height: '40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    cursor: 'pointer',
    backdropFilter: 'blur(6px)',
    padding: 0,
  },
  bar: {
    display: 'block',
    width: '22px',
    height: '2px',
    background: 'var(--text)',
    borderRadius: '1px',
    transition: 'all 0.2s',
  },
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(var(--bg-rgb),0.7)',
    zIndex: 1050,
    transition: 'opacity 0.25s',
    backdropFilter: 'blur(3px)',
  },
  drawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '260px',
    maxWidth: '85vw',
    background: 'var(--panel-deep3)',
    borderLeft: '1px solid var(--border)',
    zIndex: 1100,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'max(env(safe-area-inset-top, 0px), var(--ck-android-inset-top, 0px))',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
    boxShadow: '-8px 0 32px rgba(0,0,0,0.6)',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 18px 16px',
  },
  drawerLogo: {
    fontFamily: "'Orbitron',monospace",
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: 'var(--text)',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--muted-2)',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '2px 4px',
    lineHeight: 1,
  },
  divider: {
    height: '1px',
    background: 'var(--border)',
    margin: '0 18px',
  },
  section: {
    padding: '16px 10px 8px',
  },
  sectionLabel: {
    fontFamily: "'Orbitron',monospace",
    fontSize: '7px',
    letterSpacing: '3px',
    color: 'var(--border-2)',
    padding: '0 8px 10px',
  },
  navItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'transparent',
    border: 'none',
    borderRadius: '3px',
    padding: '11px 8px',
    fontFamily: "'Rajdhani',sans-serif",
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--muted-3)',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s',
    letterSpacing: '0.3px',
  },
  navItemActive: {
    color: '#fff',
    background: 'rgba(var(--cyan-rgb),0.07)',
  },
  navIcon: {
    fontFamily: "'Share Tech Mono',monospace",
    fontSize: '13px',
    color: 'var(--muted-2)',
    width: '18px',
    flexShrink: 0,
    textAlign: 'center',
  },
  drawerFooter: {
    marginTop: 'auto',
    padding: '16px 18px',
    borderTop: '1px solid var(--border)',
  },
  footerTxt: {
    fontFamily: "'Share Tech Mono',monospace",
    fontSize: '9px',
    color: 'var(--border-2)',
    letterSpacing: '1px',
  },
};
